import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetNextAppointmentQuery } from '@features/appointments';
import {
  useCreateEncounterFromAppointmentMutation,
  useCreateEncounterMutation,
} from '@features/encounters';
import { useRequireScheduledAppointmentForNoteCreation, useTemplatesList } from '@features/user';
import { Add as AddIcon, CalendarMonth, Description, Person } from '@mui/icons-material';
import { Box, Button, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { format, formatDistanceToNow, isToday } from 'date-fns';
import { useSnackbar } from 'notistack';

import { DashboardCard } from './DashboardCard';

const UpcomingEncountersCard: React.FC = () => {
  const navigate = useNavigate();
  const templatesList = useTemplatesList();
  const requiresScheduledAppointmentForNoteCreation =
    useRequireScheduledAppointmentForNoteCreation();
  const { enqueueSnackbar } = useSnackbar();

  // RTK Query hooks
  const [createEncounter] = useCreateEncounterMutation();
  const [createEncounterFromAppointment] = useCreateEncounterFromAppointmentMutation();

  // Use RTK Query hook to fetch next appointments
  const { data: appointmentsData, isLoading } = useGetNextAppointmentQuery({
    num: 5,
  });

  const appointments = appointmentsData?.items || [];

  const handleCreateEncounter = async () => {
    if (requiresScheduledAppointmentForNoteCreation) {
      navigate('/schedule');
      return;
    }

    try {
      const newEncounter = await createEncounter({
        createEncounterRequest: {
          encounter_type: templatesList?.[0]?.display_name,
          template_id: templatesList?.[0]?.template_id,
        },
      }).unwrap();
      navigate(`/encounters/${newEncounter.encounter_id}`);
    } catch (error) {
      console.error('Error creating encounter:', error);
      enqueueSnackbar('Error creating encounter', { variant: 'error' });
    }
  };

  const LoadingSkeleton = () => (
    <Stack spacing={2}>
      {[...(Array(3) as unknown[])].map((_, index) => (
        <Paper
          key={index}
          sx={{
            p: 2,
            backgroundColor: 'background.paper',
            border: 1,
            borderRadius: '0.75rem',
            borderColor: 'borderColors.primary',
            position: 'relative',
          }}
        >
          {/* Time indicator skeleton */}
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 80,
              height: 24,
            }}
          >
            <Skeleton
              variant="rectangular"
              sx={{ width: '100%', height: '100%', borderRadius: 2 }}
            />
          </Box>

          <Stack spacing={1}>
            {/* Patient name skeleton */}
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Skeleton variant="circular" sx={{ width: 24, height: 24 }} />
              <Skeleton variant="text" sx={{ width: '60%', height: 24 }} />
            </Stack>

            {/* Template name skeleton */}
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Skeleton variant="circular" sx={{ width: 24, height: 24 }} />
              <Skeleton variant="text" sx={{ width: '40%', height: 20 }} />
            </Stack>

            {/* Date/time skeleton */}
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Skeleton variant="circular" sx={{ width: 24, height: 24 }} />
              <Skeleton variant="text" sx={{ width: '50%', height: 20 }} />
            </Stack>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );

  return (
    <DashboardCard
      customStyles={{
        padding: 2,
        minHeight: { xs: '400px', md: '450px' },
        maxHeight: '450px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6">Next Appointments</Typography>
        {appointments.length > 0 && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateEncounter}
            size="small"
          >
            New
          </Button>
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {isLoading ? (
          <LoadingSkeleton />
        ) : appointments.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No upcoming appointments
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateEncounter}
              sx={{ mt: 2 }}
            >
              Schedule an Appointment
            </Button>
          </Box>
        ) : (
          <Stack spacing={2}>
            {appointments.map(appointment => (
              <Paper
                key={appointment.appointment_id}
                onClick={async () => {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  if (appointment.encounter?.encounter_id) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    navigate(`/encounters/${appointment.encounter.encounter_id}`);
                    return;
                  }

                  try {
                    const newEncounter = await createEncounterFromAppointment({
                      appointmentId: appointment.appointment_id,
                    }).unwrap();
                    navigate(`/encounters/${newEncounter.encounter_id}`);
                  } catch (error) {
                    console.error('Error creating encounter:', error);
                    enqueueSnackbar('Error creating encounter from appointment', {
                      variant: 'error',
                    });
                  }
                }}
                sx={{
                  p: 2,
                  backgroundColor: 'background.paper',
                  border: 1,
                  borderRadius: '0.75rem',
                  borderColor: 'borderColors.primary',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  position: 'relative',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    transform: 'translateY(-2px)',
                    boxShadow: 1,
                  },
                }}
              >
                {appointment.start_time && (() => {
                  // GDN-2784: this card offers the next appointments with no
                  // same-day restriction — a non-today row must carry its
                  // date and lose the ready-to-go green, because one click
                  // binds documentation to that appointment's encounter.
                  const start = new Date(appointment.start_time);
                  const isFutureDay = !isToday(start);
                  const tone = isFutureDay ? 'warning' : 'success';
                  return (
                    <Paper
                      elevation={0}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        px: 1.5,
                        py: 0.5,
                        bgcolor: `${tone}.soft`,
                        borderRadius: 2,
                        border: 1,
                        borderColor: `${tone}.main`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color={`${tone}.main`}
                        sx={{ fontWeight: 'medium' }}
                      >
                        {isFutureDay
                          ? `${format(start, 'EEE, MMM d')} · ${formatDistanceToNow(start, {
                              addSuffix: true,
                            })}`
                          : formatDistanceToNow(start, { addSuffix: true })}
                      </Typography>
                    </Paper>
                  );
                })()}

                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    {appointment.patient ? (
                      <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                    ) : null}
                    <Typography variant="subtitle1">
                      {appointment.patient
                        ? `${appointment.patient.name}`
                        : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                          appointment.encounter?.title || 'Untitled Encounter'}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={2}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Description sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {appointment.encounter_type || 'No Template'}
                      </Typography>
                    </Stack>
                  </Stack>

                  {appointment.start_time && (
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <CalendarMonth sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {format(new Date(appointment.start_time), "MMM d, yyyy 'at' h:mm a")}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
    </DashboardCard>
  );
};

export default UpcomingEncountersCard;
